import { useEffect, useMemo, useState } from "react";
import Layout from "../components/Layout";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { fmtCur, fmtCurCompact, fmtDateTime } from "../lib/format";
import { toast } from "sonner";
import { ChartLineUp, ClockCounterClockwise, User, X } from "@phosphor-icons/react";
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, Legend, CartesianGrid,
} from "recharts";

const BORDER = "1px solid var(--omh-border)";
const MUTED = "var(--omh-text-muted)";
const TEAL = "#0d9488";
const SLATE = "#64748b";
const fmtNum = (n) => Number(n || 0).toLocaleString("en-US");

const th = { textAlign: "right", padding: "9px 10px", fontSize: 11.5, textTransform: "uppercase",
  letterSpacing: 0.4, color: MUTED, fontWeight: 700, whiteSpace: "nowrap", borderBottom: BORDER, background: "#f8fafc" };
const td = { padding: "7px 10px", fontSize: 13, borderBottom: BORDER, whiteSpace: "nowrap" };

function Kpi({ label, value, sub, accent }) {
  return (
    <div style={{ background: "#fff", border: BORDER, borderTop: `3px solid ${accent || TEAL}`, borderRadius: 12, padding: "14px 16px", minWidth: 170 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: MUTED, textTransform: "uppercase", letterSpacing: 0.4 }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 800, marginTop: 6, color: accent || "var(--omh-text)", fontFamily: "Bogle, 'Helvetica Neue', Arial, sans-serif" }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: MUTED, marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

export default function OtherForecastView() {
  const [customers, setCustomers] = useState([]);
  const [customer, setCustomer] = useState("");
  const [data, setData] = useState(null);       // /view payload
  const [loading, setLoading] = useState(false);
  const [revs, setRevs] = useState([]);
  const [revDetail, setRevDetail] = useState(null);

  useEffect(() => {
    api.get("/forecasts/other/customers").then((r) => setCustomers(r.data.customers || [])).catch(() => setCustomers([]));
  }, []);

  const load = (name) => {
    if (!name) { setData(null); setRevs([]); return; }
    setLoading(true);
    Promise.all([
      api.get(`/forecasts/other/view?customer=${encodeURIComponent(name)}`),
      api.get(`/forecasts/other/revisions?customer=${encodeURIComponent(name)}`),
    ])
      .then(([v, rv]) => { setData(v.data); setRevs(rv.data.revisions || []); })
      .catch(() => { toast.error("Failed to load forecast"); setData(null); setRevs([]); })
      .finally(() => setLoading(false));
  };
  const onPick = (name) => { setCustomer(name); load(name); };

  const currency = data?.currency || "USD";
  const horizon = data?.horizon || [];

  const chartData = useMemo(() => {
    if (!data?.saved) return [];
    const a = (data.actual_series || []).map((p) => ({ label: p.label, actualQty: p.qty, actualVal: p.val }));
    const f = (data.forecast_series || []).map((p) => ({ label: p.label, forecastQty: p.qty, forecastVal: p.val }));
    return [...a, ...f];
  }, [data]);

  const openRev = (rev) => {
    api.get(`/forecasts/other/revision?customer=${encodeURIComponent(customer)}&rev=${rev}`)
      .then((r) => setRevDetail(r.data)).catch(() => toast.error("Failed to load revision"));
  };

  return (
    <Layout>
      <div data-testid="other-fc-view-page" style={{ padding: "12px 26px 22px", maxWidth: 1500, margin: "0 auto" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
          <h1 style={{ fontSize: 20, fontWeight: 800, display: "flex", alignItems: "center", gap: 10, margin: 0, color: "var(--omh-text)" }}>
            <ChartLineUp size={22} weight="duotone" color={TEAL} /> Other Customers — View Forecast
          </h1>
        </div>

        <div style={{ display: "flex", gap: 12, alignItems: "center", marginTop: 14, flexWrap: "wrap" }}>
          <label style={{ fontSize: 12.5, fontWeight: 700, color: MUTED }}>Customer</label>
          <select data-testid="other-fc-view-customer-select" value={customer} onChange={(e) => onPick(e.target.value)}
            style={{ padding: "9px 12px", border: BORDER, borderRadius: 9, fontSize: 13.5, background: "#fff", minWidth: 320 }}>
            <option value="">Select a customer…</option>
            {customers.map((c) => (
              <option key={c.name} value={c.name}>{c.name}{c.has_forecast ? "" : "  · no forecast yet"}</option>
            ))}
          </select>
          {data?.saved && (
            <span data-testid="other-fc-view-meta" style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, color: "#0f766e", background: "#f0fdfa", border: "1px solid #99f6e4", padding: "4px 10px", borderRadius: 20 }}>
              <User size={13} weight="bold" /> Last edited by {data.updated_by} · {fmtDateTime(data.updated_at)} · rev {data.rev}
            </span>
          )}
        </div>

        {loading ? (
          <div style={{ padding: 50, textAlign: "center", color: MUTED }}>Loading forecast…</div>
        ) : !customer ? (
          <div data-testid="other-fc-view-empty" style={{ marginTop: 26, padding: 44, textAlign: "center", border: "1px dashed var(--omh-border)", borderRadius: 14, background: "#fff" }}>
            <ChartLineUp size={38} weight="duotone" color="#94a3b8" />
            <div style={{ fontSize: 15.5, fontWeight: 700, marginTop: 10 }}>Pick a customer to view its forecast</div>
          </div>
        ) : !data?.saved ? (
          <div data-testid="other-fc-view-none" style={{ marginTop: 26, padding: 44, textAlign: "center", border: "1px dashed var(--omh-border)", borderRadius: 14, background: "#fff" }}>
            <div style={{ fontSize: 15.5, fontWeight: 700 }}>No forecast saved for {customer} yet</div>
            <Link to="/forecasts/other/new" style={{ color: TEAL, fontWeight: 700, fontSize: 13.5, marginTop: 8, display: "inline-block" }}>Create one →</Link>
          </div>
        ) : (
          <>
            {/* KPIs */}
            <div style={{ display: "flex", gap: 14, marginTop: 20, flexWrap: "wrap" }}>
              <Kpi label="Forecast Value" value={fmtCurCompact(data.total_val, currency)} sub={`${horizon.length}-month horizon`} />
              <Kpi label="Forecast Units" value={fmtNum(data.total_qty)} sub={`${data.items.length} item${data.items.length === 1 ? "" : "s"}`} accent={SLATE} />
              <Kpi label="Avg / month" value={fmtNum(Math.round((data.total_qty || 0) / (horizon.length || 1)))} sub="units" accent={SLATE} />
              <Kpi label="Horizon" value={horizon.length ? `${horizon[0].label} – ${horizon[horizon.length - 1].label}` : "—"} sub="next 6 months" accent="#1d4ed8" />
            </div>

            {/* Chart: last-6-mo actual -> next-6-mo forecast */}
            <div style={{ marginTop: 18, background: "#fff", border: BORDER, borderRadius: 12, padding: "16px 12px 6px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "0 8px 8px", fontSize: 13, fontWeight: 700, color: "var(--omh-text)" }}>
                Actual (last 6 mo) → Forecast (next 6 mo)
                <span style={{ fontSize: 11.5, fontWeight: 500, color: MUTED }}>· bars = value ({currency}), lines = units</span>
              </div>
              <ResponsiveContainer width="100%" height={330}>
                <ComposedChart data={chartData} margin={{ top: 8, right: 18, left: 6, bottom: 6 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                  <XAxis dataKey="label" tick={{ fontSize: 11, fill: MUTED }} />
                  <YAxis yAxisId="v" tick={{ fontSize: 11, fill: MUTED }} tickFormatter={(v) => fmtCurCompact(v, currency)} width={64} />
                  <YAxis yAxisId="q" orientation="right" tick={{ fontSize: 11, fill: MUTED }} tickFormatter={(v) => fmtNum(v)} width={60} />
                  <Tooltip formatter={(val, name) => {
                    if (name.includes("Value")) return [fmtCur(val, currency), name];
                    return [fmtNum(val), name];
                  }} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar yAxisId="v" dataKey="actualVal" name="Actual Value" fill="#cbd5e1" radius={[3, 3, 0, 0]} maxBarSize={34} />
                  <Bar yAxisId="v" dataKey="forecastVal" name="Forecast Value" fill={TEAL} radius={[3, 3, 0, 0]} maxBarSize={34} />
                  <Line yAxisId="q" type="monotone" dataKey="actualQty" name="Actual Units" stroke={SLATE} strokeWidth={2} dot={{ r: 3 }} connectNulls />
                  <Line yAxisId="q" type="monotone" dataKey="forecastQty" name="Forecast Units" stroke="#0f766e" strokeWidth={2} strokeDasharray="5 4" dot={{ r: 3 }} connectNulls />
                </ComposedChart>
              </ResponsiveContainer>
            </div>

            {/* Per-item table */}
            <div style={{ marginTop: 18, background: "#fff", border: BORDER, borderRadius: 12, overflow: "auto" }}>
              <table data-testid="other-fc-view-table" style={{ borderCollapse: "collapse", minWidth: 980, width: "100%" }}>
                <thead>
                  <tr>
                    <th style={{ ...th, textAlign: "left", position: "sticky", left: 0, zIndex: 2 }}>Item</th>
                    <th style={{ ...th, textAlign: "left" }}>Description</th>
                    <th style={th}>Price</th>
                    {horizon.map((m) => <th key={m.code} style={{ ...th, minWidth: 74 }}>{m.label}</th>)}
                    <th style={{ ...th, background: "#ecfdf5", color: TEAL }}>Total Qty</th>
                    <th style={{ ...th, background: "#ecfeff", color: "#0e7490" }}>Total $</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((r, i) => (
                    <tr key={r.item + i} data-testid="other-fc-view-row" style={{ background: i % 2 ? "#fbfdff" : "#fff" }}>
                      <td style={{ ...td, textAlign: "left", fontFamily: "Inter, sans-serif", fontWeight: 700, position: "sticky", left: 0, background: i % 2 ? "#fbfdff" : "#fff", zIndex: 1 }}>{r.item}</td>
                      <td style={{ ...td, textAlign: "left", maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis" }}>{r.item_desc || "—"}</td>
                      <td style={{ ...td, textAlign: "right", color: MUTED }}>{r.price ? fmtCur(r.price, currency) : "—"}</td>
                      {horizon.map((m) => (
                        <td key={m.code} style={{ ...td, textAlign: "right", color: r.months[m.code]?.qty ? "var(--omh-text)" : "#cbd5e1" }}>
                          {r.months[m.code]?.qty ? fmtNum(r.months[m.code].qty) : "·"}
                        </td>
                      ))}
                      <td style={{ ...td, textAlign: "right", fontWeight: 800, background: "#f0fdf4", color: "#15803d" }}>{fmtNum(r.total_qty)}</td>
                      <td style={{ ...td, textAlign: "right", fontWeight: 700, background: "#f0fdfa", color: TEAL }}>{r.total_val ? fmtCur(r.total_val, currency) : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Revision history */}
            <div style={{ marginTop: 22, background: "#fff", border: BORDER, borderRadius: 12, overflow: "hidden" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "12px 16px", borderBottom: BORDER, fontSize: 14, fontWeight: 800, color: "var(--omh-text)" }}>
                <ClockCounterClockwise size={18} weight="duotone" color={TEAL} /> Revision history
                <span style={{ fontSize: 12, fontWeight: 500, color: MUTED }}>· {revs.length} saved version{revs.length === 1 ? "" : "s"}</span>
              </div>
              <table data-testid="other-fc-rev-table" style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th style={{ ...th, textAlign: "left" }}>Rev</th>
                    <th style={{ ...th, textAlign: "left" }}>Saved</th>
                    <th style={{ ...th, textAlign: "left" }}>By</th>
                    <th style={th}>Items</th>
                    <th style={th}>Total Qty</th>
                    <th style={th}>Total $</th>
                    <th style={th} />
                  </tr>
                </thead>
                <tbody>
                  {revs.map((rv) => (
                    <tr key={rv.rev} data-testid="other-fc-rev-row" style={{ background: rv.rev === data.rev ? "#f0fdfa" : "#fff" }}>
                      <td style={{ ...td, textAlign: "left", fontWeight: 800 }}>#{rv.rev}{rv.rev === data.rev && <span style={{ marginLeft: 6, fontSize: 10, color: TEAL, background: "#ccfbf1", padding: "1px 7px", borderRadius: 10 }}>current</span>}</td>
                      <td style={{ ...td, textAlign: "left", color: MUTED }}>{fmtDateTime(rv.saved_at)}</td>
                      <td style={{ ...td, textAlign: "left" }}>{rv.saved_by}</td>
                      <td style={{ ...td, textAlign: "right" }}>{rv.item_count}</td>
                      <td style={{ ...td, textAlign: "right" }}>{fmtNum(rv.total_qty)}</td>
                      <td style={{ ...td, textAlign: "right", color: TEAL, fontWeight: 600 }}>{fmtCur(rv.total_val, currency)}</td>
                      <td style={{ ...td, textAlign: "right" }}>
                        <button data-testid={`other-fc-rev-open-${rv.rev}`} onClick={() => openRev(rv.rev)}
                          style={{ border: BORDER, background: "#fff", borderRadius: 7, padding: "4px 10px", fontSize: 12, fontWeight: 700, color: TEAL, cursor: "pointer" }}>View</button>
                      </td>
                    </tr>
                  ))}
                  {revs.length === 0 && <tr><td colSpan={7} style={{ ...td, textAlign: "center", color: MUTED, padding: 22 }}>No revisions.</td></tr>}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* Revision detail modal */}
        {revDetail && (
          <div data-testid="other-fc-rev-modal" onClick={() => setRevDetail(null)}
            style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,.45)", display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "6vh 16px", zIndex: 60 }}>
            <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: 14, maxWidth: 1000, width: "100%", maxHeight: "84vh", overflow: "auto", boxShadow: "0 24px 60px rgba(0,0,0,.28)" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 18px", borderBottom: BORDER, position: "sticky", top: 0, background: "#fff" }}>
                <div style={{ fontSize: 15, fontWeight: 800 }}>
                  {revDetail.customer_name} · Revision #{revDetail.rev}
                  <span style={{ fontSize: 12, fontWeight: 500, color: MUTED, marginLeft: 8 }}>saved by {revDetail.saved_by} · {fmtDateTime(revDetail.saved_at)}</span>
                </div>
                <button data-testid="other-fc-rev-modal-close" onClick={() => setRevDetail(null)} style={{ border: "none", background: "transparent", cursor: "pointer", color: MUTED }}><X size={20} weight="bold" /></button>
              </div>
              <div style={{ padding: 16, overflow: "auto" }}>
                <table style={{ borderCollapse: "collapse", width: "100%", minWidth: 900 }}>
                  <thead>
                    <tr>
                      <th style={{ ...th, textAlign: "left" }}>Item</th>
                      <th style={{ ...th, textAlign: "left" }}>Description</th>
                      <th style={th}>Price</th>
                      {(revDetail.horizon || []).map((m) => <th key={m.code} style={{ ...th, minWidth: 72 }}>{m.label}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {(revDetail.items || []).map((it, i) => (
                      <tr key={it.item + i} style={{ background: i % 2 ? "#fbfdff" : "#fff" }}>
                        <td style={{ ...td, textAlign: "left", fontFamily: "Inter, sans-serif", fontWeight: 700 }}>{it.item}</td>
                        <td style={{ ...td, textAlign: "left", maxWidth: 240, overflow: "hidden", textOverflow: "ellipsis" }}>{it.item_desc || "—"}</td>
                        <td style={{ ...td, textAlign: "right", color: MUTED }}>{it.price ? fmtCur(it.price, revDetail.currency) : "—"}</td>
                        {(revDetail.horizon || []).map((m) => (
                          <td key={m.code} style={{ ...td, textAlign: "right", color: it.months?.[m.code] ? "var(--omh-text)" : "#cbd5e1" }}>
                            {it.months?.[m.code] ? fmtNum(it.months[m.code]) : "·"}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
